import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Empty,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ApartmentOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  getAvailableModules,
  getModuleAutomations,
  saveModuleAutomations,
} from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
import AppCard from '../../../components/cards/AppCard';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

// =============================================================================
// Automations — the Admin surface for models.ModuleStrategies (Be_Auth_DevOps).
// This is the config-driven replacement for logic legacy services used to
// hardcode in Go: reference-id generation, duplicate checks, cross-module
// field snapshots, reporting-chain hierarchy expansion (assign a job up to
// admin, notify a submission up the whole chain), board updates, mail,
// cache invalidation. Nothing here is module-specific — every field maps
// directly onto models.ModuleConfig.Gateway / .Strategies.
// =============================================================================

// ── Shared value-map row editors ────────────────────────────────────────────
// Two flavours: PlainFieldMapRows keeps values as plain strings (used for
// FieldMap-type maps: sourceField -> destField, both dot-paths, never
// templated). KeyValueRows attempts JSON.parse on save so a value can be a
// literal (number/bool/array/object) OR a "{{field.path}}" template token —
// exactly what resolveTemplateValue on the backend expects.

function PlainFieldMapRows({ rows, onChange, keyPlaceholder, valuePlaceholder }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { key: '', value: '' }]);
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <Input size="small" style={{ flex: 1 }} placeholder={keyPlaceholder} value={r.key} onChange={(e) => update(i, { key: e.target.value })} className="fg-mono-input" />
          <span style={{ color: '#94a3b8', alignSelf: 'center' }}>→</span>
          <Input size="small" style={{ flex: 1 }} placeholder={valuePlaceholder} value={r.value} onChange={(e) => update(i, { value: e.target.value })} className="fg-mono-input" />
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
        </div>
      ))}
      <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 6 }} onClick={add}>Add</Button>
    </div>
  );
}

function KeyValueRows({ rows, onChange, keyPlaceholder = 'field', valuePlaceholder = 'literal or {{field.path}}' }) {
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { key: '', value: '' }]);
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <Input size="small" style={{ flex: 1 }} placeholder={keyPlaceholder} value={r.key} onChange={(e) => update(i, { key: e.target.value })} className="fg-mono-input" />
          <Input size="small" style={{ flex: 1.6 }} placeholder={valuePlaceholder} value={r.value} onChange={(e) => update(i, { value: e.target.value })} className="fg-mono-input" />
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(i)} />
        </div>
      ))}
      <Button size="small" type="dashed" icon={<PlusOutlined />} style={{ marginTop: 6 }} onClick={add}>Add</Button>
      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
        Values are read as JSON when possible (true, 123, ["a","b"]) — otherwise kept as literal text.
        Use <code>{'{{field.path}}'}</code> to reference a payload field, or <code>{'{{now}}'}</code> for the current time.
      </Text>
    </div>
  );
}

function objToPlainRows(obj) {
  return Object.entries(obj ?? {}).map(([key, value]) => ({ key, value: String(value ?? '') }));
}

function plainRowsToObj(rows) {
  const out = {};
  for (const r of rows ?? []) {
    if (!r.key?.trim()) continue;
    out[r.key.trim()] = r.value ?? '';
  }
  return out;
}

function objToRows(obj) {
  return Object.entries(obj ?? {}).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
}

function rowsToObj(rows) {
  const out = {};
  for (const r of rows ?? []) {
    if (!r.key?.trim()) continue;
    let v = r.value;
    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (trimmed !== '') {
        try { v = JSON.parse(trimmed); } catch { /* keep as raw string — a template token or plain literal */ }
      }
    }
    out[r.key.trim()] = v;
  }
  return out;
}

// ── Denormalize Rule Drawer ─────────────────────────────────────────────────
function DenormalizeDrawer({ open, rule, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    sourceModule: '', sourceIdField: '', required: false,
    ...(rule ?? {}),
    fieldMapRows: objToPlainRows(rule?.fieldMap),
  }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.sourceModule?.trim()) return message.error('Source module is required');
    if (!draft.sourceIdField?.trim()) return message.error('Source id field is required');
    const { fieldMapRows, ...rest } = draft;
    onSave({ ...rest, fieldMap: plainRowsToObj(fieldMapRows) });
  };

  return (
    <Modal
      open={open} onCancel={onClose} width={640} centered destroyOnClose maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><DatabaseOutlined /> {rule ? 'Edit Denormalize Rule' : 'New Denormalize Rule'}</span>}
      footer={<div className="fg-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button></div>}
    >
      <div className="fg-form-section">
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Source module</Text>
            <Input size="small" placeholder="job" value={draft.sourceModule ?? ''} onChange={(e) => set('sourceModule', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Source id field (on THIS payload)</Text>
            <Input size="small" placeholder="jobId" value={draft.sourceIdField ?? ''} onChange={(e) => set('sourceIdField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!draft.required} onChange={(v) => set('required', v)} />
          <Text style={{ fontSize: 13 }}>Required — block the write if the source record can't be found</Text>
        </div>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Field map (source field → this payload's field)</Text>
          <PlainFieldMapRows
            rows={draft.fieldMapRows ?? []}
            onChange={(rows) => set('fieldMapRows', rows)}
            keyPlaceholder="jobTitle"
            valuePlaceholder="jobTitle"
          />
        </div>
      </div>
    </Modal>
  );
}

// ── Array Enrich Drawer ──────────────────────────────────────────────────────
function ArrayEnrichDrawer({ open, rule, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    sourceField: '', targetField: '', sourceCollection: '', idField: '', idOutputKey: '', stampNow: '',
    ...(rule ?? {}),
    fieldMapRows: objToPlainRows(rule?.fieldMap),
  }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.sourceField?.trim()) return message.error('Source field is required');
    if (!draft.targetField?.trim()) return message.error('Target field is required');
    const { fieldMapRows, ...rest } = draft;
    onSave({ ...rest, fieldMap: plainRowsToObj(fieldMapRows) });
  };

  return (
    <Modal
      open={open} onCancel={onClose} width={640} centered destroyOnClose maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><ApartmentOutlined /> {rule ? 'Edit Array Enrichment' : 'New Array Enrichment'}</span>}
      footer={<div className="fg-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button></div>}
    >
      <div className="fg-form-section">
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Source field (array of ids on this payload)</Text>
            <Input size="small" placeholder="recruitersData" value={draft.sourceField ?? ''} onChange={(e) => set('sourceField', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Target field (enriched array written here)</Text>
            <Input size="small" placeholder="recruiters" value={draft.targetField ?? ''} onChange={(e) => set('targetField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Source collection</Text>
            <Input size="small" placeholder="users (default)" value={draft.sourceCollection ?? ''} onChange={(e) => set('sourceCollection', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Id field on that collection</Text>
            <Input size="small" placeholder="legacyUserId (default)" value={draft.idField ?? ''} onChange={(e) => set('idField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Field map (source collection field → output key)</Text>
          <PlainFieldMapRows
            rows={draft.fieldMapRows ?? []}
            onChange={(rows) => set('fieldMapRows', rows)}
            keyPlaceholder="firstName"
            valuePlaceholder="recruiterName"
          />
        </div>
        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Also write the matched id as</Text>
            <Input size="small" placeholder="recruiterId" value={draft.idOutputKey ?? ''} onChange={(e) => set('idOutputKey', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Also stamp the current time as</Text>
            <Input size="small" placeholder="assignedDateTime" value={draft.stampNow ?? ''} onChange={(e) => set('stampNow', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── File Field Map Drawer ─────────────────────────────────────────────────────
function FileFieldMapDrawer({ open, rule, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    sourceField: '', targetField: '', uploadedFileNameField: '', uniqueNameField: '',
    uploadedAtField: '', uploadedByField: '', arrayMode: false, nameFromField: '', nameOutputField: '',
    historyField: '', historySnapshotKey: '', historyReason: '',
    ...(rule ?? {}),
    staticFieldRows: objToPlainRows(rule?.staticFields),
    arrayFieldNameRows: objToPlainRows(rule?.arrayFieldNames),
  }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.sourceField?.trim()) return message.error('Source field (the multipart upload key) is required');
    if (!draft.targetField?.trim()) return message.error('Target field is required');
    const { staticFieldRows, arrayFieldNameRows, ...rest } = draft;
    onSave({
      ...rest,
      staticFields: plainRowsToObj(staticFieldRows),
      arrayFieldNames: plainRowsToObj(arrayFieldNameRows),
    });
  };

  return (
    <Modal
      open={open} onCancel={onClose} width={760} centered destroyOnClose maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><DatabaseOutlined /> {rule ? 'Edit File Field Map' : 'New File Field Map'}</span>}
      footer={<div className="fg-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button></div>}
    >
      <div className="fg-form-section">
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
          Turns one uploaded file field into a nested object (or array of objects) instead of a flat S3 path string —
          for schemas like a candidate's resume/passport/visa that store location + filename + who/when as sub-fields.
        </Text>
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Source field (multipart upload key)</Text>
            <Input size="small" placeholder="resume" value={draft.sourceField ?? ''} onChange={(e) => set('sourceField', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Target field {draft.arrayMode ? '(array)' : '(location sub-field)'}</Text>
            <Input size="small" placeholder={draft.arrayMode ? 'documents' : 'resume.location'} value={draft.targetField ?? ''} onChange={(e) => set('targetField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!draft.arrayMode} onChange={(v) => set('arrayMode', v)} />
          <Text style={{ fontSize: 13 }}>Array mode — every uploaded file becomes one element (e.g. "documents")</Text>
        </div>

        {draft.arrayMode ? (
          <>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sub-field names (location / uploadedFileName / uniqueName → this schema's names)</Text>
              <PlainFieldMapRows rows={draft.arrayFieldNameRows ?? []} onChange={(rows) => set('arrayFieldNameRows', rows)} keyPlaceholder="location" valuePlaceholder="documentCopyLocation" />
            </div>
            <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Take each element's display name from (payload array field)</Text>
                <Input size="small" placeholder="documents" value={draft.nameFromField ?? ''} onChange={(e) => set('nameFromField', e.target.value)} className="fg-mono-input" />
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>...reading its field</Text>
                <Input size="small" placeholder="documentName" value={draft.nameOutputField ?? ''} onChange={(e) => set('nameOutputField', e.target.value)} className="fg-mono-input" />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Original filename field</Text>
                <Input size="small" placeholder="resume.uploadedFileName" value={draft.uploadedFileNameField ?? ''} onChange={(e) => set('uploadedFileNameField', e.target.value)} className="fg-mono-input" />
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Unique name field</Text>
                <Input size="small" placeholder="resume.uniqueName" value={draft.uniqueNameField ?? ''} onChange={(e) => set('uniqueNameField', e.target.value)} className="fg-mono-input" />
              </div>
            </div>
            <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Uploaded-at field</Text>
                <Input size="small" placeholder="resume.uploadedAt" value={draft.uploadedAtField ?? ''} onChange={(e) => set('uploadedAtField', e.target.value)} className="fg-mono-input" />
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Uploaded-by field</Text>
                <Input size="small" placeholder="resume.uploadedBy" value={draft.uploadedByField ?? ''} onChange={(e) => set('uploadedByField', e.target.value)} className="fg-mono-input" />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Static fields (literal value written alongside the upload)</Text>
              <PlainFieldMapRows rows={draft.staticFieldRows ?? []} onChange={(rows) => set('staticFieldRows', rows)} keyPlaceholder="resume.source" valuePlaceholder="candidate" />
            </div>
            <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Append a snapshot to (history array field)</Text>
                <Input size="small" placeholder="resumeHistory" value={draft.historyField ?? ''} onChange={(e) => set('historyField', e.target.value)} className="fg-mono-input" />
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>...under snapshot key</Text>
                <Input size="small" placeholder="resume" value={draft.historySnapshotKey ?? ''} onChange={(e) => set('historySnapshotKey', e.target.value)} className="fg-mono-input" />
              </div>
              <div style={{ flex: 1 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>...with reason</Text>
                <Input size="small" placeholder="Initial resume upload" value={draft.historyReason ?? ''} onChange={(e) => set('historyReason', e.target.value)} className="fg-mono-input" />
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ── Hierarchy Expand Drawer ──────────────────────────────────────────────────
function HierarchyExpandDrawer({ open, rule, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    name: '', seedField: '', direction: 'up', stopRole: 0, includeSeed: true,
    ...(rule ?? {}),
  }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.name?.trim()) return message.error('Name is required — this is how RelatedUpdates/Notify reference the result');
    if (!/^[a-zA-Z0-9_]+$/.test(draft.name)) return message.error('Name: letters, numbers, underscores only');
    onSave(draft);
  };

  return (
    <Modal
      open={open} onCancel={onClose} width={620} centered destroyOnClose maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><ApartmentOutlined /> {rule ? 'Edit Hierarchy Expansion' : 'New Hierarchy Expansion'}</span>}
      footer={<div className="fg-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button></div>}
    >
      <div className="fg-form-section">
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Name</Text>
            <Input size="small" placeholder="chain" value={draft.name ?? ''} onChange={(e) => set('name', e.target.value)} className="fg-mono-input" />
            <Text type="secondary" style={{ fontSize: 11 }}>Referenced as <code>{'{{hierarchy.' + (draft.name || 'name') + '}}'}</code></Text>
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Seed field (blank = the caller)</Text>
            <Input size="small" placeholder="recruitersData" value={draft.seedField ?? ''} onChange={(e) => set('seedField', e.target.value)} className="fg-mono-input" />
          </div>
        </div>
        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Direction</Text>
            <Select
              size="small" style={{ width: '100%' }} value={draft.direction || 'up'}
              onChange={(v) => set('direction', v)}
              options={[
                { value: 'up', label: 'Up — toward admin (reporting chain)' },
                { value: 'down', label: 'Down — toward subordinates (team)' },
              ]}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Stop at role id (up only, 0 = walk to the top)</Text>
            <InputNumber size="small" style={{ width: '100%' }} min={0} value={draft.stopRole ?? 0} onChange={(v) => set('stopRole', v ?? 0)} disabled={draft.direction !== 'up'} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={draft.includeSeed !== false} onChange={(v) => set('includeSeed', v)} />
          <Text style={{ fontSize: 13 }}>Include the seed user(s) themselves in the result</Text>
        </div>
      </div>
    </Modal>
  );
}

// ── Related Update Drawer ────────────────────────────────────────────────────
function RelatedUpdateDrawer({ open, rule, hierarchyNames, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({
    collectionName: '', targetModule: '', matchField: '', scopeTenant: false,
    forEach: '', insertMode: !!(rule?.insertIfMissing && Object.keys(rule.insertIfMissing).length),
    ...(rule ?? {}),
    extraFilterRows: objToRows(rule?.extraFilter),
    pullRows: objToRows(rule?.pull),
    pushRows: objToRows(rule?.push),
    setRows: objToRows(rule?.set),
    setIfNonBlankRows: objToRows(rule?.setIfNonBlank),
    insertIfMissingRows: objToRows(rule?.insertIfMissing),
  }));
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleSave = () => {
    if (!draft.collectionName?.trim() && !draft.targetModule?.trim()) {
      return message.error('Set either a collection name or a target module');
    }
    const {
      extraFilterRows, pullRows, pushRows, setRows, setIfNonBlankRows, insertIfMissingRows, insertMode,
      ...rest
    } = draft;
    const out = {
      ...rest,
      extraFilter: rowsToObj(extraFilterRows),
      pull: insertMode ? {} : rowsToObj(pullRows),
      push: insertMode ? {} : rowsToObj(pushRows),
      set: insertMode ? {} : rowsToObj(setRows),
      setIfNonBlank: insertMode ? {} : rowsToObj(setIfNonBlankRows),
      insertIfMissing: insertMode ? rowsToObj(insertIfMissingRows) : {},
    };
    onSave(out);
  };

  const foreachOptions = (hierarchyNames ?? []).map((n) => ({ value: `hierarchy.${n}`, label: `{{hierarchy.${n}}}` }));

  return (
    <Modal
      open={open} onCancel={onClose} width={820} centered destroyOnClose maskClosable={false}
      className="fg-editor-modal"
      title={<span className="fg-drawer-title"><ThunderboltOutlined /> {rule ? 'Edit Related Update' : 'New Related Update'}</span>}
      footer={<div className="fg-drawer-footer"><Button onClick={onClose}>Cancel</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Apply</Button></div>}
    >
      <div className="fg-form-section">
        <div className="fg-form-section-label">Target</div>
        <div className="fg-field-meta-row">
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Collection name (used verbatim)</Text>
            <Input size="small" placeholder="hiringProgress" value={draft.collectionName ?? ''} onChange={(e) => set('collectionName', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>— or — target module</Text>
            <Input size="small" placeholder="job" value={draft.targetModule ?? ''} onChange={(e) => set('targetModule', e.target.value)} className="fg-mono-input" />
          </div>
        </div>

        <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Match field (this payload's field holding the target _id)</Text>
            <Input size="small" placeholder="jobId (blank for a tenant-scoped board)" value={draft.matchField ?? ''} onChange={(e) => set('matchField', e.target.value)} className="fg-mono-input" />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Repeat once per element of (ForEach)</Text>
            <Select
              size="small" style={{ width: '100%' }} allowClear showSearch
              placeholder="e.g. {{hierarchy.chain}}"
              value={draft.forEach || undefined}
              onChange={(v) => set('forEach', v ?? '')}
              options={foreachOptions}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <Switch size="small" checked={!!draft.scopeTenant} onChange={(v) => set('scopeTenant', v)} />
          <Text style={{ fontSize: 13 }}>Scope filter to the caller's tenant/business/businessUnit</Text>
        </div>

        <div style={{ marginTop: 14 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Extra filter (merged into the match filter)</Text>
          <KeyValueRows rows={draft.extraFilterRows ?? []} onChange={(rows) => set('extraFilterRows', rows)} keyPlaceholder="recruiterId" valuePlaceholder='{"$in":"{{hierarchy.chain}}"}' />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
          <Switch size="small" checked={!!draft.insertMode} onChange={(v) => set('insertMode', v)} />
          <Text style={{ fontSize: 13 }}>Insert-only-if-missing (seed a new document instead of updating an existing one)</Text>
        </div>

        {draft.insertMode ? (
          <div style={{ marginTop: 10 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Document to insert when the filter matches nothing</Text>
            <KeyValueRows rows={draft.insertIfMissingRows ?? []} onChange={(rows) => set('insertIfMissingRows', rows)} keyPlaceholder="hiringProgressJobStatus" valuePlaceholder='"unread"' />
          </div>
        ) : (
          <Tabs
            size="small"
            style={{ marginTop: 10 }}
            items={[
              { key: 'pull', label: 'Pull', children: <KeyValueRows rows={draft.pullRows ?? []} onChange={(rows) => set('pullRows', rows)} keyPlaceholder="submitted" valuePlaceholder='{"candidateId":"{{applicantId}}"}' /> },
              { key: 'push', label: 'Push', children: <KeyValueRows rows={draft.pushRows ?? []} onChange={(rows) => set('pushRows', rows)} keyPlaceholder="submitted" valuePlaceholder='{"status":"{{submissionStatus}}"}' /> },
              { key: 'set', label: 'Set', children: <KeyValueRows rows={draft.setRows ?? []} onChange={(rows) => set('setRows', rows)} keyPlaceholder="hiringProgressJobStatus" valuePlaceholder='"unread"' /> },
              { key: 'setIfNonBlank', label: 'Set if non-blank', children: <KeyValueRows rows={draft.setIfNonBlankRows ?? []} onChange={(rows) => set('setIfNonBlankRows', rows)} keyPlaceholder="resume.location" valuePlaceholder="{{resume.location}}" /> },
            ]}
          />
        )}
      </div>
    </Modal>
  );
}

// ── Generic list-of-rules table (Denormalize / ArrayEnrich / HierarchyExpand / RelatedUpdate) ──
function RuleListCard({ icon, title, description, rules, columns, onAdd, onEdit, onDelete }) {
  return (
    <AppCard
      title={<span>{icon} {title}</span>}
      extra={<Button size="small" type="dashed" icon={<PlusOutlined />} onClick={onAdd}>Add</Button>}
      styles={{ body: { paddingTop: 8 } }}
    >
      {description && <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>{description}</Text>}
      <Table
        rowKey={(r, i) => i}
        dataSource={rules}
        columns={[...columns, {
          title: '', key: 'actions', width: 80, align: 'right',
          render: (_, r, i) => (
            <Space size={4}>
              <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(i, r)} /></Tooltip>
              <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(i)} /></Tooltip>
            </Space>
          ),
        }]}
        pagination={false}
        size="small"
        className="fg-table"
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Not configured" /> }}
      />
    </AppCard>
  );
}

// ── Main Section ──────────────────────────────────────────────────────────────
const emptyStrategies = {
  sequence: null,
  duplicateCheck: null,
  denormalizations: [],
  arrayEnrichments: [],
  fileFieldMaps: [],
  sanitizeFields: [],
  defaults: {},
  hierarchyExpands: [],
  relatedUpdates: [],
  notify: null,
  cacheInvalidate: [],
};

export default function AutomationsSection() {
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [selected, setSelected] = useState('');

  const [gateway, setGateway] = useState({ storage: 'mongo' });
  const [originalStorage, setOriginalStorage] = useState('mongo');
  const [strategies, setStrategies] = useState(emptyStrategies);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [denormDraft, setDenormDraft] = useState(null); // { index, rule } | { index: null }
  const [arrayEnrichDraft, setArrayEnrichDraft] = useState(null);
  const [fileFieldMapDraft, setFileFieldMapDraft] = useState(null);
  const [hierarchyDraft, setHierarchyDraft] = useState(null);
  const [relatedUpdateDraft, setRelatedUpdateDraft] = useState(null);

  useEffect(() => {
    getAvailableModules()
      .then((raw) => {
        const mods = normalizeModuleOptions(raw).map(({ value, label }) => ({ key: value, label }));
        setModules(mods);
        if (mods.length) setSelected(mods[0].key);
      })
      .catch(() => setModules([]))
      .finally(() => setModulesLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      try {
        const cfg = await getModuleAutomations(selected);
        if (cancelled) return;
        const gw = cfg.gateway ?? { storage: 'mongo' };
        setGateway(gw);
        setOriginalStorage(gw.storage || 'mongo');
        setStrategies({ ...emptyStrategies, ...(cfg.strategies ?? {}) });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load automations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const hierarchyNames = useMemo(() => (strategies.hierarchyExpands ?? []).map((h) => h.name).filter(Boolean), [strategies.hierarchyExpands]);

  function setStrategy(key, value) {
    setStrategies((prev) => ({ ...prev, [key]: value }));
  }

  function doSave() {
    setSaving(true);
    const payload = {
      ...strategies,
      denormalizations: strategies.denormalizations?.length ? strategies.denormalizations : undefined,
      arrayEnrichments: strategies.arrayEnrichments?.length ? strategies.arrayEnrichments : undefined,
      fileFieldMaps: strategies.fileFieldMaps?.length ? strategies.fileFieldMaps : undefined,
      sanitizeFields: strategies.sanitizeFields?.length ? strategies.sanitizeFields : undefined,
      hierarchyExpands: strategies.hierarchyExpands?.length ? strategies.hierarchyExpands : undefined,
      relatedUpdates: strategies.relatedUpdates?.length ? strategies.relatedUpdates : undefined,
      cacheInvalidate: strategies.cacheInvalidate?.length ? strategies.cacheInvalidate : undefined,
      defaults: Object.keys(strategies.defaults ?? {}).length ? strategies.defaults : undefined,
    };
    saveModuleAutomations(selected, { gateway, strategies: payload })
      .then(() => {
        setOriginalStorage(gateway.storage || 'mongo');
        message.success('Automations saved');
      })
      .catch((e) => message.error(e?.message || 'Could not save automations'))
      .finally(() => setSaving(false));
  }

  function handleSave() {
    if ((gateway.storage || 'mongo') !== originalStorage) {
      Modal.confirm({
        title: 'Change how this module persists writes?',
        content: `You're switching ${selected} from "${originalStorage}" to "${gateway.storage}". ` +
          (gateway.storage === 'mongo'
            ? 'This module will stop calling its downstream service and this gateway will own the write + every Strategy below.'
            : 'This module will stop using the Strategies below and go back to calling its downstream service.') +
          ' Make sure the downstream service / e2e checks are ready before confirming.',
        okText: 'Confirm cutover',
        okButtonProps: { danger: true },
        onOk: doSave,
      });
      return;
    }
    doSave();
  }

  const denormColumns = [
    { title: 'Source module', dataIndex: 'sourceModule', key: 'sourceModule', render: (v) => <code className="fg-table-code">{v}</code> },
    { title: 'Source id field', dataIndex: 'sourceIdField', key: 'sourceIdField' },
    { title: 'Fields', key: 'fields', render: (_, r) => <Badge count={Object.keys(r.fieldMap ?? {}).length} showZero color="var(--color-brand)" /> },
    { title: 'Required', dataIndex: 'required', key: 'required', width: 90, render: (v) => (v ? <Tag color="volcano">Required</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>optional</Text>) },
  ];

  const arrayEnrichColumns = [
    { title: 'Source field', dataIndex: 'sourceField', key: 'sourceField', render: (v) => <code className="fg-table-code">{v}</code> },
    { title: 'Target field', dataIndex: 'targetField', key: 'targetField', render: (v) => <code className="fg-table-code">{v}</code> },
    { title: 'From collection', key: 'coll', render: (_, r) => r.sourceCollection || 'users' },
    { title: 'Fields', key: 'fields', render: (_, r) => <Badge count={Object.keys(r.fieldMap ?? {}).length} showZero color="var(--color-brand)" /> },
  ];

  const fileFieldMapColumns = [
    { title: 'Source field', dataIndex: 'sourceField', key: 'sourceField', render: (v) => <code className="fg-table-code">{v}</code> },
    { title: 'Target field', dataIndex: 'targetField', key: 'targetField', render: (v) => <code className="fg-table-code">{v}</code> },
    { title: 'Mode', dataIndex: 'arrayMode', key: 'arrayMode', width: 100, render: (v) => (v ? <Tag color="blue">Array</Tag> : <Tag>Single</Tag>) },
    { title: 'History', dataIndex: 'historyField', key: 'historyField', render: (v) => v || <Text type="secondary" style={{ fontSize: 12 }}>—</Text> },
  ];

  const hierarchyColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v) => <code className="fg-table-code">{'{{hierarchy.' + v + '}}'}</code> },
    { title: 'Seed field', dataIndex: 'seedField', key: 'seedField', render: (v) => v || <Text type="secondary" style={{ fontSize: 12 }}>caller</Text> },
    { title: 'Direction', dataIndex: 'direction', key: 'direction', render: (v) => <Tag color={v === 'down' ? 'blue' : 'purple'}>{v === 'down' ? 'Down — team' : 'Up — chain'}</Tag> },
    { title: 'Stop role', dataIndex: 'stopRole', key: 'stopRole', width: 90, render: (v) => v || <Text type="secondary" style={{ fontSize: 12 }}>top</Text> },
  ];

  const relatedUpdateColumns = [
    { title: 'Target', key: 'target', render: (_, r) => <code className="fg-table-code">{r.collectionName || r.targetModule}</code> },
    { title: 'Match', key: 'match', render: (_, r) => r.matchField || <Text type="secondary" style={{ fontSize: 12 }}>tenant-scoped</Text> },
    { title: 'For each', key: 'forEach', render: (_, r) => (r.forEach ? <Tag color="geekblue">{`{{${r.forEach}}}`}</Tag> : <Text type="secondary" style={{ fontSize: 12 }}>once</Text>) },
    { title: 'Mode', key: 'mode', render: (_, r) => (Object.keys(r.insertIfMissing ?? {}).length ? <Tag color="green">Insert if missing</Tag> : <Tag>Update</Tag>) },
  ];

  const moduleTabs = modules.map((m) => ({ key: m.key, label: m.label }));

  return (
    <div className="fg-section">
      <AppCard
        icon={<ThunderboltOutlined />}
        title="Automations"
        extra={
          <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} disabled={!selected} onClick={handleSave}>
            Save Automations
          </Button>
        }
      >
        <div className="admin-accent-bar" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          Config-driven side-effects the common CRUD gateway runs around a create — reference ids, duplicate checks,
          cross-module field snapshots, reporting-chain hierarchy expansion, board updates, notifications, cache
          invalidation. Nothing here is module-specific: the same engine serves every module.
        </Text>
      </AppCard>

      <AppCard styles={{ body: { paddingTop: 8 } }}>
        <Spin spinning={modulesLoading}>
          <Tabs activeKey={selected} onChange={setSelected} items={moduleTabs.map((t) => ({ ...t, children: null }))} className="admin-module-tabs" />
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

          <Spin spinning={loading}>
            {/* ── Gateway (the write-path switch) ───────────────────────────── */}
            <AppCard title="Gateway" styles={{ body: { paddingTop: 8 } }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                Selects how a write to this module persists. "Proxy" forwards to a downstream microservice (which
                owns every side-effect below itself). "Mongo" makes THIS gateway own the write and run every
                Strategy configured below.
              </Text>
              <div className="fg-field-meta-row">
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Storage</Text>
                  <Select
                    size="small" style={{ width: '100%' }} value={gateway.storage || 'mongo'}
                    onChange={(v) => setGateway((g) => ({ ...g, storage: v }))}
                    options={[{ value: 'mongo', label: 'Mongo — this gateway owns the write' }, { value: 'proxy', label: 'Proxy — forward to a downstream service' }]}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>File field key</Text>
                  <Input size="small" placeholder="file" value={gateway.fileKey ?? ''} onChange={(e) => setGateway((g) => ({ ...g, fileKey: e.target.value }))} className="fg-mono-input" />
                </div>
              </div>
              {gateway.storage === 'proxy' && (
                <>
                  <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Base URL env var</Text>
                      <Input size="small" placeholder="JOBS_SERVICE_URL" value={gateway.baseUrlEnv ?? ''} onChange={(e) => setGateway((g) => ({ ...g, baseUrlEnv: e.target.value }))} className="fg-mono-input" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Base URL (fallback)</Text>
                      <Input size="small" placeholder="http://..." value={gateway.baseUrl ?? ''} onChange={(e) => setGateway((g) => ({ ...g, baseUrl: e.target.value }))} className="fg-mono-input" />
                    </div>
                  </div>
                  <div className="fg-field-meta-row" style={{ marginTop: 12 }}>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Create path</Text>
                      <Input size="small" placeholder="/v1/jobs" value={gateway.createPath ?? ''} onChange={(e) => setGateway((g) => ({ ...g, createPath: e.target.value }))} className="fg-mono-input" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Update path (%s = id)</Text>
                      <Input size="small" placeholder="/v1/edit/%s" value={gateway.updatePath ?? ''} onChange={(e) => setGateway((g) => ({ ...g, updatePath: e.target.value }))} className="fg-mono-input" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Update method</Text>
                      <Select size="small" style={{ width: '100%' }} value={gateway.updateMethod || 'PUT'} onChange={(v) => setGateway((g) => ({ ...g, updateMethod: v }))} options={['PUT', 'POST', 'PATCH'].map((m) => ({ value: m, label: m }))} />
                    </div>
                  </div>
                </>
              )}
            </AppCard>

            {/* ── Sequence ─────────────────────────────────────────────────── */}
            <AppCard title="Reference ID (Sequence)" styles={{ body: { paddingTop: 8 } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Switch size="small" checked={!!strategies.sequence} onChange={(v) => setStrategy('sequence', v ? { field: '', prefix: '', counterKey: '' } : null)} />
                <Text style={{ fontSize: 13 }}>Generate a "{'{prefix}{n}'}" reference id on create</Text>
              </div>
              {strategies.sequence && (
                <div className="fg-field-meta-row">
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Payload field</Text>
                    <Input size="small" placeholder="submissionId" value={strategies.sequence.field ?? ''} onChange={(e) => setStrategy('sequence', { ...strategies.sequence, field: e.target.value })} className="fg-mono-input" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Prefix</Text>
                    <Input size="small" placeholder="sub" value={strategies.sequence.prefix ?? ''} onChange={(e) => setStrategy('sequence', { ...strategies.sequence, prefix: e.target.value })} className="fg-mono-input" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Counter key</Text>
                    <Input size="small" placeholder="submissions" value={strategies.sequence.counterKey ?? ''} onChange={(e) => setStrategy('sequence', { ...strategies.sequence, counterKey: e.target.value })} className="fg-mono-input" />
                  </div>
                </div>
              )}
            </AppCard>

            {/* ── Duplicate Check ──────────────────────────────────────────── */}
            <AppCard title="Duplicate Check" styles={{ body: { paddingTop: 8 } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Switch size="small" checked={!!strategies.duplicateCheck} onChange={(v) => setStrategy('duplicateCheck', v ? { matchFields: [], message: '' } : null)} />
                <Text style={{ fontSize: 13 }}>Block a create when an active record already matches</Text>
              </div>
              {strategies.duplicateCheck && (
                <div className="fg-field-meta-row">
                  <div style={{ flex: 1.4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Match fields</Text>
                    <Select mode="tags" size="small" style={{ width: '100%' }} placeholder="jobId, applicantId" value={strategies.duplicateCheck.matchFields ?? []} onChange={(v) => setStrategy('duplicateCheck', { ...strategies.duplicateCheck, matchFields: v })} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Message</Text>
                    <Input size="small" placeholder="submission exists" value={strategies.duplicateCheck.message ?? ''} onChange={(e) => setStrategy('duplicateCheck', { ...strategies.duplicateCheck, message: e.target.value })} />
                  </div>
                </div>
              )}
            </AppCard>

            {/* ── Denormalizations ─────────────────────────────────────────── */}
            <RuleListCard
              icon={<DatabaseOutlined />}
              title="Denormalizations"
              description="Copy fields from ANOTHER module's record onto this payload before insert (e.g. a submission snapshotting job title/client)."
              rules={strategies.denormalizations ?? []}
              columns={denormColumns}
              onAdd={() => setDenormDraft({ index: null, rule: null })}
              onEdit={(i, r) => setDenormDraft({ index: i, rule: r })}
              onDelete={(i) => setStrategy('denormalizations', (strategies.denormalizations ?? []).filter((_, idx) => idx !== i))}
            />

            {/* ── Array Enrichments ────────────────────────────────────────── */}
            <RuleListCard
              icon={<ApartmentOutlined />}
              title="Array Enrichments"
              description="Enrich a payload array of ids (e.g. recruitersData) into objects carrying looked-up fields (name, email) before insert."
              rules={strategies.arrayEnrichments ?? []}
              columns={arrayEnrichColumns}
              onAdd={() => setArrayEnrichDraft({ index: null, rule: null })}
              onEdit={(i, r) => setArrayEnrichDraft({ index: i, rule: r })}
              onDelete={(i) => setStrategy('arrayEnrichments', (strategies.arrayEnrichments ?? []).filter((_, idx) => idx !== i))}
            />

            {/* ── File Field Maps ──────────────────────────────────────────── */}
            <RuleListCard
              icon={<DatabaseOutlined />}
              title="File Field Maps"
              description="Turn an uploaded file field into a nested object (location + filename + who/when) or an array of such objects, instead of a flat S3 path string — needed for schemas like resume/passport/visa."
              rules={strategies.fileFieldMaps ?? []}
              columns={fileFieldMapColumns}
              onAdd={() => setFileFieldMapDraft({ index: null, rule: null })}
              onEdit={(i, r) => setFileFieldMapDraft({ index: i, rule: r })}
              onDelete={(i) => setStrategy('fileFieldMaps', (strategies.fileFieldMaps ?? []).filter((_, idx) => idx !== i))}
            />

            {/* ── Sanitize Fields ──────────────────────────────────────────── */}
            <AppCard title="Sanitize Fields" styles={{ body: { paddingTop: 8 } }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Trim and HTML-escape these payload fields before anything else runs — basic XSS-safety for free-text
                fields (e.g. firstName/lastName) that might later be embedded in an HTML activity comment or email.
              </Text>
              <Select
                mode="tags" style={{ width: '100%' }}
                placeholder="firstName, lastName, email"
                value={strategies.sanitizeFields ?? []}
                onChange={(v) => setStrategy('sanitizeFields', v)}
              />
            </AppCard>

            {/* ── Defaults ─────────────────────────────────────────────────── */}
            <AppCard title="Defaults" styles={{ body: { paddingTop: 8 } }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Set a field only when the payload doesn't already carry a non-blank value — status defaults, empty arrays.
              </Text>
              <KeyValueRows
                rows={objToRows(strategies.defaults)}
                onChange={(rows) => setStrategy('defaults', rowsToObj(rows))}
                keyPlaceholder="internalSubmissionStatus.internalSubmissionStatusText"
                valuePlaceholder='"Under Evaluation"'
              />
            </AppCard>

            {/* ── Hierarchy Expansions ─────────────────────────────────────── */}
            <RuleListCard
              icon={<ApartmentOutlined />}
              title="Hierarchy Expansions"
              description="Walk the reporting chain up (toward admin) or down (toward a team) from a seed user, publishing the result as {{hierarchy.<name>}} for Related Updates / Notify to use."
              rules={strategies.hierarchyExpands ?? []}
              columns={hierarchyColumns}
              onAdd={() => setHierarchyDraft({ index: null, rule: null })}
              onEdit={(i, r) => setHierarchyDraft({ index: i, rule: r })}
              onDelete={(i) => setStrategy('hierarchyExpands', (strategies.hierarchyExpands ?? []).filter((_, idx) => idx !== i))}
            />

            {/* ── Related Updates ──────────────────────────────────────────── */}
            <RuleListCard
              icon={<ThunderboltOutlined />}
              title="Related Updates"
              description="Best-effort side-effect writes to other collections after a successful insert — board state, activity logs. Combine with ForEach + a Hierarchy Expansion to seed one document per user in a chain."
              rules={strategies.relatedUpdates ?? []}
              columns={relatedUpdateColumns}
              onAdd={() => setRelatedUpdateDraft({ index: null, rule: null })}
              onEdit={(i, r) => setRelatedUpdateDraft({ index: i, rule: r })}
              onDelete={(i) => setStrategy('relatedUpdates', (strategies.relatedUpdates ?? []).filter((_, idx) => idx !== i))}
            />

            {/* ── Notify ───────────────────────────────────────────────────── */}
            <AppCard title="Notify" styles={{ body: { paddingTop: 8 } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Switch size="small" checked={!!strategies.notify} onChange={(v) => setStrategy('notify', v ? { action: '', subjectDefault: '', bodyDefault: '', toReportingManager: false, toHierarchy: '' } : null)} />
                <Text style={{ fontSize: 13 }}>Send a best-effort email after a successful insert</Text>
              </div>
              {strategies.notify && (
                <>
                  <div className="fg-field-meta-row">
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Action (matched against an EmailTemplates doc)</Text>
                      <Input size="small" placeholder="internalSubmit" value={strategies.notify.action ?? ''} onChange={(e) => setStrategy('notify', { ...strategies.notify, action: e.target.value })} className="fg-mono-input" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Notify entire hierarchy chain</Text>
                      <Select size="small" style={{ width: '100%' }} allowClear placeholder="pick a Hierarchy Expansion" value={strategies.notify.toHierarchy || undefined} onChange={(v) => setStrategy('notify', { ...strategies.notify, toHierarchy: v ?? '' })} options={hierarchyNames.map((n) => ({ value: n, label: n }))} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                    <Switch size="small" checked={!!strategies.notify.toReportingManager} onChange={(v) => setStrategy('notify', { ...strategies.notify, toReportingManager: v })} />
                    <Text style={{ fontSize: 13 }}>Also notify the caller's direct reporting manager</Text>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Default subject</Text>
                    <Input size="small" placeholder="New submission" value={strategies.notify.subjectDefault ?? ''} onChange={(e) => setStrategy('notify', { ...strategies.notify, subjectDefault: e.target.value })} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>Default body (used when no EmailTemplates doc matches)</Text>
                    <Input.TextArea rows={4} value={strategies.notify.bodyDefault ?? ''} onChange={(e) => setStrategy('notify', { ...strategies.notify, bodyDefault: e.target.value })} className="fg-mono-input" />
                  </div>
                </>
              )}
            </AppCard>

            {/* ── Cache Invalidate ─────────────────────────────────────────── */}
            <AppCard title="Cache Invalidate" styles={{ body: { paddingTop: 8 } }}>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Redis key patterns (with {'{{field}}'} tokens) to SCAN+DEL after a successful insert.
              </Text>
              <Select
                mode="tags" style={{ width: '100%' }}
                placeholder="jobs:{{tenantId}}:{{businessId}}:{{businessUnitId}}:*"
                value={strategies.cacheInvalidate ?? []}
                onChange={(v) => setStrategy('cacheInvalidate', v)}
              />
            </AppCard>
          </Spin>
        </Spin>
      </AppCard>

      <DenormalizeDrawer
        key={denormDraft ? `denorm-${denormDraft.index ?? 'new'}` : 'denorm-closed'}
        open={!!denormDraft} rule={denormDraft?.rule} onClose={() => setDenormDraft(null)}
        onSave={(rule) => {
          setStrategy('denormalizations', denormDraft.index === null
            ? [...(strategies.denormalizations ?? []), rule]
            : (strategies.denormalizations ?? []).map((r, i) => (i === denormDraft.index ? rule : r)));
          setDenormDraft(null);
        }}
      />

      <ArrayEnrichDrawer
        key={arrayEnrichDraft ? `enrich-${arrayEnrichDraft.index ?? 'new'}` : 'enrich-closed'}
        open={!!arrayEnrichDraft} rule={arrayEnrichDraft?.rule} onClose={() => setArrayEnrichDraft(null)}
        onSave={(rule) => {
          setStrategy('arrayEnrichments', arrayEnrichDraft.index === null
            ? [...(strategies.arrayEnrichments ?? []), rule]
            : (strategies.arrayEnrichments ?? []).map((r, i) => (i === arrayEnrichDraft.index ? rule : r)));
          setArrayEnrichDraft(null);
        }}
      />

      <FileFieldMapDrawer
        key={fileFieldMapDraft ? `filemap-${fileFieldMapDraft.index ?? 'new'}` : 'filemap-closed'}
        open={!!fileFieldMapDraft} rule={fileFieldMapDraft?.rule} onClose={() => setFileFieldMapDraft(null)}
        onSave={(rule) => {
          setStrategy('fileFieldMaps', fileFieldMapDraft.index === null
            ? [...(strategies.fileFieldMaps ?? []), rule]
            : (strategies.fileFieldMaps ?? []).map((r, i) => (i === fileFieldMapDraft.index ? rule : r)));
          setFileFieldMapDraft(null);
        }}
      />

      <HierarchyExpandDrawer
        key={hierarchyDraft ? `hier-${hierarchyDraft.index ?? 'new'}` : 'hier-closed'}
        open={!!hierarchyDraft} rule={hierarchyDraft?.rule} onClose={() => setHierarchyDraft(null)}
        onSave={(rule) => {
          setStrategy('hierarchyExpands', hierarchyDraft.index === null
            ? [...(strategies.hierarchyExpands ?? []), rule]
            : (strategies.hierarchyExpands ?? []).map((r, i) => (i === hierarchyDraft.index ? rule : r)));
          setHierarchyDraft(null);
        }}
      />

      <RelatedUpdateDrawer
        key={relatedUpdateDraft ? `ru-${relatedUpdateDraft.index ?? 'new'}` : 'ru-closed'}
        open={!!relatedUpdateDraft} rule={relatedUpdateDraft?.rule} hierarchyNames={hierarchyNames} onClose={() => setRelatedUpdateDraft(null)}
        onSave={(rule) => {
          setStrategy('relatedUpdates', relatedUpdateDraft.index === null
            ? [...(strategies.relatedUpdates ?? []), rule]
            : (strategies.relatedUpdates ?? []).map((r, i) => (i === relatedUpdateDraft.index ? rule : r)));
          setRelatedUpdateDraft(null);
        }}
      />
    </div>
  );
}
